import React, { Fragment } from "react";
import { connect } from "react-redux";
import { get } from "lodash";
import { Col } from "react-bootstrap";
import PropTypes from "prop-types";
import { change, Field } from "redux-form";
import {
  AutoCompleteField,
  FormInput,
  Select
} from "MODULES/shared/components/Forms";
import countries from "LIB/countries";
import { states } from "LIB/states";
import FormRow from "MODULES/shared/components/Forms/FormRow";
import {
  CITY,
  COMPANY_ID,
  COMPANY_NAME,
  COUNTRY,
  LINE1,
  LINE2,
  POSTAL_CODE,
  STATE
} from "APP_ROOT/constants";
import { APPROVAL_STATUSES } from "MODULES/companies/constants";
import * as actions from "MODULES/ManagedRepairOrders/actions";
import classNames from "classnames";
import Contact from "./Contact";

export const countryOptions = countries.map(c => ({
  value: c.code,
  text: c.name
}));

const renderStateField = (country, path, required = false) =>
  country === "US" ? (
    <Field
      name={`${path}.${STATE}`}
      component={Select}
      options={states}
      label="State/Province"
      required={required}
    />
  ) : (
    <Field
      name={`${path}.${STATE}`}
      component={FormInput}
      label="State/Province"
      required={required}
    />
  );

export class FreightForwarderAddressFields extends React.Component {
  state = {
    newCompany: false,
    existingCompany: true,
    activeCompany: "existing",
    selectedCompany: this.props.freightForwarder
  };

  componentWillReceiveProps(nextProps) {
    if (this.props.country && nextProps.country !== this.props.country) {
      this.props.resetStateField();
    }
  }

  handleNewCompany = () => {
    this.setState({
      activeCompany: "new",
      newCompany: true,
      existingCompany: false,
      selectedCompany: null
    });
    this.props.clearFreightForwarderFields();
  };

  handleExistingCompany = () => {
    this.setState({
      activeCompany: "existing",
      newCompany: false,
      existingCompany: true,
      selectedCompany: null
    });
    this.props.clearFreightForwarderFields();
  };

  handleCompanySelection = selectedCompany => {
    const { dispatch, formName, path } = this.props;
    this.setState({ selectedCompany });

    if (selectedCompany) {
      const fields = [LINE1, LINE2, CITY, STATE, POSTAL_CODE, COUNTRY];
      const addressObj =
        selectedCompany.primary_physical_address ||
        selectedCompany.billing_address;

      fields.forEach(field => {
        dispatch(
          change(
            formName,
            `${path}.${field}`,
            get(addressObj, field, "") // fallback to ""
          )
        );
      });
    }
  };

  formatItem = result => {
    if (!get(result, "name", get(result, "company_name"))) return null;
    const label = get(APPROVAL_STATUSES, `${result.vendor_status}.label`);
    return `${result.name || result.company_name || ""}${label
      ? ` (${label})`
      : ""}`;
  };

  render() {
    const { path, country, required } = this.props;
    const { newCompany, selectedCompany } = this.state;

    const activeExistingCompany = classNames("btn btn-default", {
      active: this.state.activeCompany === "existing"
    });
    const activeNewCompany = classNames("btn btn-default", {
      active: this.state.activeCompany === "new"
    });

    return (
      <Fragment>
        <FormRow>
          <Col md={4}>
            <div className="form-group">
              <div
                className="btn-group btn-group-justified"
                data-toggle="buttons"
                role="group"
              >
                <div
                  className={activeExistingCompany}
                  onClick={this.handleExistingCompany}
                >
                  <i className="fa fa-lg fa-check-circle" />
                  &nbsp;&nbsp;Existing Company
                </div>
                <div
                  className={activeNewCompany}
                  onClick={this.handleNewCompany}
                >
                  <i className="fa fa-lg fa-check-circle" />
                  &nbsp;&nbsp;New Company
                </div>
              </div>
            </div>
          </Col>
        </FormRow>
        <FormRow>
          <Col md={6}>
            <h4>Physical Address</h4>
          </Col>
        </FormRow>
        <FormRow>
          <Col md={6}>
            {newCompany ? (
              <Field
                name={`${path}.${COMPANY_NAME}`}
                label="New Company Name"
                placeholder="New Company Name"
                component={FormInput}
                required
              />
            ) : null}
            {this.state.existingCompany && (
              <Field
                name={`${path}.${COMPANY_ID}`}
                label="Existing Company Name"
                placeholder="Search by typing a company's name or code"
                component={AutoCompleteField}
                search={this.props.searchCompanies}
                selectedItem={selectedCompany}
                formatItem={this.formatItem}
                required={required}
                onItemSelected={this.handleCompanySelection}
              />
            )}
          </Col>
          <Col md={6}>
            <Field
              name={`${path}.${COUNTRY}`}
              component={Select}
              label="Country"
              options={countryOptions}
              required
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col md={6}>
            <Field
              name={`${path}.${LINE1}`}
              component={FormInput}
              label="Address Line 1"
              required
            />
          </Col>
          <Col md={6}>
            <Field
              name={`${path}.${LINE2}`}
              component={FormInput}
              label="Address Line 2"
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col md={5}>
            <Field
              name={`${path}.${CITY}`}
              component={FormInput}
              label="City"
              required
            />
          </Col>
          <Col md={4}>{renderStateField(country, path, country === "US")}</Col>
          <Col md={3}>
            <Field
              name={`${path}.${POSTAL_CODE}`}
              component={FormInput}
              label="Zip/Postal Code"
              required={country === "US"}
            />
          </Col>
        </FormRow>
        <Contact path="freight_forwarder" required={false} />
      </Fragment>
    );
  }
}

FreightForwarderAddressFields.propTypes = {
  path: PropTypes.string.isRequired,
  country: PropTypes.string,
  resetStateField: PropTypes.func.isRequired,
  required: PropTypes.bool
};

export default connect(undefined, (dispatch, ownProps) => ({
  dispatch,

  resetStateField: () =>
    dispatch(change(ownProps.formName, `${ownProps.path}.${STATE}`, "")),

  searchCompanies: filter => dispatch(actions.searchCompanies(filter)),

  /**
     * Clears ALL freight-forwarder fields (company + address).
     * Called whenever the user toggles to "New Company" or "Existing Company."
     */
  clearFreightForwarderFields: () => {
    const p = ownProps.path;
    const f = ownProps.formName;

    dispatch(change(f, `${p}.${COMPANY_ID}`, null));
    dispatch(change(f, `${p}.${COMPANY_NAME}`, ""));
    dispatch(change(f, `${p}.${LINE1}`, ""));
    dispatch(change(f, `${p}.${LINE2}`, ""));
    dispatch(change(f, `${p}.${CITY}`, ""));
    dispatch(change(f, `${p}.${STATE}`, ""));
    dispatch(change(f, `${p}.${POSTAL_CODE}`, ""));
    dispatch(change(f, `${p}.${COUNTRY}`, ""));
  }
}))(FreightForwarderAddressFields);



// WEBPACK FOOTER //
// ./app/modules/shared/components/CommercialInvoice/Form/FormSections/FreightForwarderAddressFields.jsx